import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

export const EmailDebugPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    console.log(message);
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testEmail = async () => {
    setLogs([]);
    setLoading(true);
    
    try {
      addLog("🚀 Starting email test...");
      addLog(`📧 Target email: ${email}`);
      addLog(`🔗 Supabase URL: ${supabase.supabaseUrl}`);
      
      const startTime = Date.now();
      
      addLog("📤 Calling supabase.functions.invoke...");
      
      const response = await supabase.functions.invoke("send-email", {
        body: {
          to: email,
          subject: "Test Email from GreenCard",
          html: "<h1>Test Email</h1><p>If you see this, it works!</p>",
        },
      });

      const endTime = Date.now();
      addLog(`⏱️ Response received in ${endTime - startTime}ms`);
      
      addLog(`📦 Response object: ${JSON.stringify(response, null, 2)}`);
      
      const { data, error } = response;
      
      if (error) {
        addLog(`❌ Error detected: ${error.message}`);
        addLog(`❌ Error details: ${JSON.stringify(error, null, 2)}`);
      } else if (data) {
        addLog(`✅ Data received: ${JSON.stringify(data, null, 2)}`);
        
        if (data.success) {
          addLog(`✅ Email sent successfully!`);
        } else {
          addLog(`⚠️ Email sending failed: ${data.error}`);
        }
      } else {
        addLog(`⚠️ No data or error returned`);
      }
      
    } catch (err) {
      addLog(`💥 Exception caught: ${err}`);
      addLog(`💥 Exception type: ${typeof err}`);
      addLog(`💥 Exception details: ${JSON.stringify(err, null, 2)}`);
    } finally {
      setLoading(false);
      addLog("✋ Test completed");
    }
  };

  const checkEdgeFunction = async () => {
    setLogs([]);
    addLog("🔍 Checking edge function deployment...");
    
    try {
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/send-email`,
        {
          method: 'OPTIONS',
          headers: {
            'apikey': supabase.supabaseKey,
          }
        }
      );
      
      addLog(`Status: ${response.status} ${response.statusText}`);
      addLog(`Headers: ${JSON.stringify([...response.headers.entries()])}`);
      
      if (response.status === 200) {
        addLog("✅ Edge function is deployed and responding to CORS");
      } else {
        addLog("❌ Edge function may not be deployed correctly");
      }
    } catch (err) {
      addLog(`❌ Error checking function: ${err}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>Email Function Debugger</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Test Email Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Test Email Address</label>
            <Input
              type="email"
              placeholder="your-email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button 
              onClick={testEmail} 
              disabled={!email || loading}
              className="flex-1"
            >
              {loading ? 'Sending...' : '📧 Send Test Email'}
            </Button>
            
            <Button 
              onClick={checkEdgeFunction} 
              variant="outline"
              className="flex-1"
            >
              🔍 Check Function Deployment
            </Button>
          </div>

          {/* Logs Display */}
          {logs.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold mb-2">Debug Logs:</h3>
              <div className="bg-black text-green-400 p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto">
                {logs.map((log, i) => (
                  <div key={i} className="mb-1">{log}</div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 p-4 rounded-lg text-sm">
            <h3 className="font-semibold mb-2">Debugging Steps:</h3>
            <ol className="list-decimal list-inside space-y-1">
              <li>First click "Check Function Deployment" to verify the edge function exists</li>
              <li>Enter your email address</li>
              <li>Click "Send Test Email"</li>
              <li>Watch the logs above for detailed information</li>
              <li>Also open browser DevTools (F12) → Console for additional logs</li>
              <li>Check Supabase Dashboard → Edge Functions → send-email → Logs</li>
            </ol>
          </div>

          {/* Environment Check */}
          <div className="bg-gray-100 p-4 rounded-lg text-sm">
            <h3 className="font-semibold mb-2">Environment Info:</h3>
            <pre className="text-xs">
{`Supabase URL: ${supabase.supabaseUrl}
Function Endpoint: ${supabase.supabaseUrl}/functions/v1/send-email
Has API Key: ${!!supabase.supabaseKey}
Browser: ${navigator.userAgent}`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};